import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useAddressStoreActions, useDeliveryAddress } from '@/stores/useAddressStore'
import {
  useDateStoreActions,
  useDeliveryDateContainer,
  usePickupDateBag,
  usePickupDateContainer,
} from '@/stores/useDateStore'

import { TermsAndConditions } from '@/app/[locale]/(common)/terms-and-conditions'

import { Input } from '@/app/[locale]/(ui)/input'
import { Textarea } from '@/app/[locale]/(ui)/textarea'
import { Button } from '@/app/[locale]/(ui)/button'
import { DateInput } from '@/app/[locale]/(ui)/date-input'
import { AddressInput } from '@/app/[locale]/(ui)/address-input'
import { ArrowRight } from 'lucide-react'
import { useLocale, useTranslations } from 'next-intl'
import { GuestOrder, GuestOrderForm, GuestOrderFormSchema } from '@/types/orders'
import { ValidationAlert } from '@/app/[locale]/(common)/validation-alert'
import { ContainerPriceTable } from '@/app/[locale]/(shop)/bestill-borettslag/container-price-table'
import { CustomerType } from '@/types/customers'
import { localeNames } from '@/i18n/routing'
import { format } from 'date-fns'
import { nb } from 'date-fns/locale'

export const OrderForm = ({
  onSubmit,
  customerType,
}: {
  onSubmit: (order: GuestOrder) => void
  customerType: CustomerType
}) => {
  const t = useTranslations()
  const locale = useLocale()

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      organizationNumber: '',
      companyName: '',
      projectNumber: '',
      name: '',
      billingAddress: '',
      email: '',
      phoneNumber: '',
      orderBag: '',
      orderContainer: '',
      comments: '',
    },
    resolver: zodResolver(GuestOrderFormSchema),
    mode: 'onSubmit',
  })
  const [isValidDeliveryAddress, setIsValidDeliveryAddress] = useState(false)
  const deliveryAddress = useDeliveryAddress()
  const { setDeliveryAddress } = useAddressStoreActions()
  const pickupDateBag = usePickupDateBag()
  const pickupDateContainer = usePickupDateContainer()
  const deliveryDateContainer = useDeliveryDateContainer()
  const { setPickupDateBag, setPickupDateContainer, setDeliveryDateContainer } = useDateStoreActions()
  const [termsChecked, setTermsChecked] = useState(false)
  const title = customerType === 'housing' ? t('Borettslag_order_form_title') : t('Company_order_form_title')
  const hasValidationErrors = Object.keys(errors).length > 0

  const onFormSubmit = (order: GuestOrderForm) => {
    if (!deliveryAddress) return

    const guestOrder: GuestOrder = {
      ...order,
      serviceAddress: `${deliveryAddress.adressetekst}, ${deliveryAddress.postnummer} ${deliveryAddress.poststed}`,
      userLocale: localeNames[locale],
      userType: customerType === 'business' ? 'Bedrift' : 'Borettslag',
      pickupDateBag: pickupDateBag && format(new Date(pickupDateBag), 'dd.MM.yyyy', { locale: nb }),
      pickupDateContainer: pickupDateContainer && format(new Date(pickupDateContainer), 'dd.MM.yyyy', { locale: nb }),
      deliveryDateContainer:
        deliveryDateContainer && format(new Date(deliveryDateContainer), 'dd.MM.yyyy', { locale: nb }),
      orderCreatedAt: format(new Date(), 'PPPPpp', { locale: nb }),
    }
    onSubmit(guestOrder)
    reset()
  }

  return (
    <>
      <h1 className="text-center text-2xl font-bold sm:text-4xl">{title}</h1>
      {customerType === 'housing' && <ContainerPriceTable />}
      <form className="mt-8 flex flex-col gap-4" onSubmit={handleSubmit(onFormSubmit)}>
        <Controller
          name="organizationNumber"
          control={control}
          render={({ field }) => (
            <Input
              label={t('Organization_number')}
              placeholder={t('Organization_number')}
              error={errors.organizationNumber && t('Invalid_organization_number')}
              required
              {...field}
            />
          )}
        />
        <Controller
          name="companyName"
          control={control}
          render={({ field }) => (
            <Input
              label={t('Company_name')}
              placeholder={t('Company_name')}
              error={errors.companyName && t('Invalid_company_name')}
              required
              {...field}
            />
          )}
        />
        <Controller
          name="projectNumber"
          control={control}
          render={({ field }) => <Input label={t('Project_number')} placeholder={t('Project_number')} {...field} />}
        />
        <Controller
          name="billingAddress"
          control={control}
          render={({ field }) => (
            <Input
              label={t('Billing_address')}
              placeholder={t('Billing_address')}
              error={errors.billingAddress && t('Please_fill_in_all_the_required_address_fields')}
              required
              {...field}
            />
          )}
        />
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <Input
              label={t('Name')}
              placeholder={t('Name')}
              error={errors.name && t('Invalid_name')}
              {...field}
              required
            />
          )}
        />
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <Input
              label={t('Email')}
              placeholder={t('Email')}
              error={errors.email && t('Invalid_email_address')}
              {...field}
              required
            />
          )}
        />
        <Controller
          name="phoneNumber"
          control={control}
          render={({ field }) => (
            <Input
              label={t('Phone_number')}
              placeholder={t('Phone_number')}
              error={errors.phoneNumber && t('Invalid_phone_number')}
              {...field}
              required
            />
          )}
        />
        <AddressInput
          label={t('Service_Address')}
          whenAddressIsSelected={(address) => setDeliveryAddress(address)}
          handleClearAddress={() => setDeliveryAddress(undefined)}
          preSelectedAddress={deliveryAddress}
          isValidAddress={isValidDeliveryAddress}
          setIsValidAddress={setIsValidDeliveryAddress}
          isB2bOrder
          required
        />
        <Controller
          name="orderBag"
          control={control}
          render={({ field }) => (
            <Textarea
              label={t('Order_for_bag')}
              placeholder={
                customerType === 'housing'
                  ? t('Borettslag_order_for_bag_helper_text')
                  : t('Company_order_for_bag_helper_text')
              }
              {...field}
            />
          )}
        />
        {watch('orderBag') && (
          <label>
            {t('Pickup_date_bags')}
            <DateInput
              date={pickupDateBag ? new Date(pickupDateBag) : undefined}
              onChange={(newDate?: Date) => setPickupDateBag(newDate ? newDate.toISOString() : '')}
            />
          </label>
        )}
        <Controller
          name="orderContainer"
          control={control}
          render={({ field }) => (
            <Input
              label={customerType === 'housing' ? t('Borettslag_order_for_container') : t('Order_for_container')}
              placeholder={
                customerType === 'housing'
                  ? t('Borettslag_order_for_container_helper_text')
                  : t('Company_order_for_container_helper_text')
              }
              {...field}
            />
          )}
        />
        {watch('orderContainer') && (
          <>
            <label>
              {t('Deliver_date_container')}
              <DateInput
                date={deliveryDateContainer ? new Date(deliveryDateContainer) : undefined}
                onChange={(newDate?: Date) => setDeliveryDateContainer(newDate ? newDate.toISOString() : '')}
              />
            </label>
            <label>
              {t('Pickup_date_container')}
              <DateInput
                date={pickupDateContainer ? new Date(pickupDateContainer) : undefined}
                minDate={deliveryDateContainer ? new Date(deliveryDateContainer) : undefined}
                onChange={(newDate?: Date) => setPickupDateContainer(newDate ? newDate.toISOString() : '')}
              />
            </label>
          </>
        )}
        <Controller
          name="comments"
          control={control}
          render={({ field }) => (
            <Textarea
              label={t('Comments')}
              placeholder={customerType === 'housing' ? t('Borettslag_comments_field') : t('Comments_helper_text')}
              {...field}
            />
          )}
        />
        <TermsAndConditions checked={termsChecked} onChange={setTermsChecked} />
        {hasValidationErrors && <ValidationAlert />}
        <Button variant="primary" type="submit" className="w-full" disabled={!termsChecked}>
          {t('Submit')}
          <ArrowRight size={20} />
        </Button>
      </form>
    </>
  )
}
